if (!customElements.get('collection-load-more')) {
  class CollectionLoadMore extends HTMLElement {
    connectedCallback() {
      this.stateVersion = 2;
      this.stateTtl = 6 * 60 * 60 * 1000;
      this.maxRestoredPages = 20;
      this.link = this.querySelector('[data-load-more-link]');
      this.status = this.querySelector('[data-load-more-status]');
      if (!this.link) return;

      this.loading = false;
      this.restoring = false;
      this.stateRestored = false;
      this.requestId = 0;
      this.querySignature = this.getQuerySignature(window.location.href);
      this.legacyStateKey = `vk-collection-load-more:${this.querySignature}`;
      this.stateKey = `vk-collection-load-more:v${this.stateVersion}:${this.querySignature}`;
      this.initialProductCount = this.getProductCount();
      this.loadedPageCount = 0;
      this.hasLoadedMore = false;
      this.autoLoadPaused = false;
      this.onClick = this.onClick.bind(this);
      this.onCollectionBeforeRender = this.onCollectionBeforeRender.bind(this);
      this.onPageHide = this.onPageHide.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.onIntersection = this.onIntersection.bind(this);
      this.link.addEventListener('click', this.onClick);
      document.addEventListener('collection:before-render', this.onCollectionBeforeRender);
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('pageshow', this.onPageShow);
      this.clearLegacyState();
      this.beginRestore();
    }

    disconnectedCallback() {
      this.link?.removeEventListener('click', this.onClick);
      document.removeEventListener('collection:before-render', this.onCollectionBeforeRender);
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('pageshow', this.onPageShow);
      this.observer?.disconnect();
      this.abortRequest();
    }

    onClick(event) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (this.loading) return;
      this.autoLoadPaused = false;
      this.loadNextPage();
    }

    onCollectionBeforeRender() {
      this.abortRequest();
      this.setLoading(false);
      this.clearState();
      this.loadedPageCount = 0;
      this.hasLoadedMore = false;
    }

    onPageHide() {
      this.saveState();
    }

    onPageShow(event) {
      if (event.persisted) {
        this.startObserving();
        return;
      }

      this.beginRestore();
    }

    onIntersection(entries) {
      if (this.autoLoadPaused || this.loading || this.restoring || !this.link) return;
      if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
    }

    startObserving() {
      if (
        !this.link ||
        this.restoring ||
        !('IntersectionObserver' in window) ||
        navigator.connection?.saveData
      ) return;

      this.observer ??= new IntersectionObserver(this.onIntersection, {
        rootMargin: '0px 0px 320px 0px',
      });
      this.observer.observe(this);
    }

    async beginRestore() {
      if (this.restoring || this.stateRestored || !this.isConnected) return;

      this.restoring = true;
      this.observer?.unobserve(this);

      try {
        await this.restoreState();
      } finally {
        this.restoring = false;
        this.stateRestored = true;
        this.startObserving();
      }
    }

    async loadNextPage({ isRestore = false } = {}) {
      const productGrid = document.getElementById('product-grid');
      if (
        !productGrid ||
        !this.link ||
        this.loading ||
        (this.restoring && !isRestore) ||
        this.getQuerySignature(window.location.href) !== this.querySignature
      ) {
        return false;
      }

      const requestId = ++this.requestId;
      const controller = new AbortController();
      const requestUrl = new URL(this.link.href, window.location.origin);
      this.observer?.unobserve(this);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);
      this.controller = controller;
      this.setLoading(true, !isRestore);
      let didLoad = false;

      try {
        const response = await fetch(requestUrl.toString(), {
          signal: controller.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Collection load more request failed with status ${response.status}`);

        const responseText = await response.text();
        if (!this.isCurrentRequest(requestId)) return;

        const parsedDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const nextGrid = parsedDocument.getElementById('product-grid');
        if (!nextGrid) throw new Error('Collection load more response is missing the product grid');

        const nextCards = Array.from(nextGrid.children).filter((card) => card.hasAttribute('data-product-id'));
        if (nextCards.length === 0) throw new Error('Collection load more response contains no product cards');

        const existingProductIds = new Set(
          Array.from(productGrid.querySelectorAll('[data-product-id]'), (card) => card.dataset.productId)
        );
        const fragment = document.createDocumentFragment();
        const appendedCards = [];

        nextCards.forEach((card) => {
          const productId = card.dataset.productId;
          if (!productId || existingProductIds.has(productId)) return;
          existingProductIds.add(productId);
          const importedCard = document.importNode(card, true);
          appendedCards.push(importedCard);
          fragment.appendChild(importedCard);
        });

        productGrid.appendChild(fragment);
        if (typeof initializeScrollAnimationTrigger === 'function' && appendedCards.length > 0) {
          initializeScrollAnimationTrigger(productGrid);
        }

        const nextLink = parsedDocument.querySelector('collection-load-more [data-load-more-link]');
        const nextHref = nextLink?.getAttribute('href');
        if (nextLink && !nextHref) throw new Error('Collection load more response is missing the next page URL');

        if (nextHref) {
          this.link.href = this.getStorefrontUrl(nextHref);
          if (!isRestore) this.announce(this.dataset.loadedMessage.replace('[count]', appendedCards.length));
        } else {
          if (!isRestore) this.announce(this.dataset.completeMessage);
          this.link.remove();
          this.link = null;
        }

        this.loadedPageCount += 1;
        this.hasLoadedMore = this.getProductCount() > this.initialProductCount;
        didLoad = true;
        if (!isRestore) this.saveState();
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected && requestId === this.requestId) {
          console.error(error);
          this.autoLoadPaused = true;
          if (!isRestore) this.announce(this.dataset.errorMessage);
        }
      } finally {
        if (this.isConnected && requestId === this.requestId) {
          this.controller = null;
          this.setLoading(false);
          if (!this.restoring) this.startObserving();
        }
      }

      return didLoad;
    }

    isCurrentRequest(requestId) {
      return (
        this.isConnected &&
        requestId === this.requestId &&
        this.getQuerySignature(window.location.href) === this.querySignature
      );
    }

    abortRequest() {
      this.requestId += 1;
      this.controller?.abort();
      this.controller = null;
    }

    setLoading(isLoading, announceLoading = true) {
      this.loading = isLoading;
      this.toggleAttribute('aria-busy', isLoading);
      if (!this.link) return;
      this.link.classList.toggle('loading', isLoading);
      this.link.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
      this.link.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      if (isLoading && announceLoading) this.announce(this.dataset.loadingMessage);
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }

    getQuerySignature(urlValue) {
      const url = new URL(urlValue, window.location.origin);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      url.searchParams.sort();
      return `${url.pathname}?${url.searchParams.toString()}`;
    }

    getStorefrontUrl(urlValue) {
      const url = new URL(urlValue, window.location.origin);
      url.searchParams.delete('section_id');
      return `${url.pathname}${url.search}${url.hash}`;
    }

    getProductGrid() {
      return document.getElementById('product-grid');
    }

    getProductCount() {
      return this.getProductGrid()?.querySelectorAll('[data-product-id]').length ?? 0;
    }

    getSavedState() {
      try {
        const savedState = JSON.parse(window.sessionStorage.getItem(this.stateKey));
        const isExpired =
          typeof savedState?.savedAt !== 'number' || Date.now() - savedState.savedAt > this.stateTtl;
        const hasValidPageCount =
          Number.isInteger(savedState?.loadedPageCount) && savedState.loadedPageCount > 0;

        if (
          !savedState ||
          savedState.version !== this.stateVersion ||
          savedState.querySignature !== this.querySignature ||
          isExpired ||
          !hasValidPageCount
        ) {
          this.clearState();
          return null;
        }

        return savedState;
      } catch (error) {
        this.clearState();
        return null;
      }
    }

    saveState() {
      if (!this.hasLoadedMore || this.loadedPageCount < 1) return;

      try {
        window.sessionStorage.setItem(
          this.stateKey,
          JSON.stringify({
            version: this.stateVersion,
            querySignature: this.querySignature,
            loadedPageCount: this.loadedPageCount,
            scrollY: window.scrollY,
            savedAt: Date.now(),
          })
        );
      } catch (error) {
        // Session storage is optional; the current page still keeps its loaded products.
      }
    }

    async restoreState() {
      const savedState = this.getSavedState();
      const productGrid = this.getProductGrid();
      if (!savedState || !productGrid) return;

      const pagesToRestore = Math.min(savedState.loadedPageCount, this.maxRestoredPages);
      this.loadedPageCount = 0;

      for (let pageIndex = 0; pageIndex < pagesToRestore; pageIndex += 1) {
        if (!this.link || !this.isConnected) break;
        const didLoad = await this.loadNextPage({ isRestore: true });
        if (!didLoad) break;
      }

      if (typeof savedState.scrollY === 'number' && savedState.scrollY > 0) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => window.scrollTo({ top: savedState.scrollY, behavior: 'auto' }));
        });
      }
    }

    clearLegacyState() {
      try {
        window.sessionStorage.removeItem(this.legacyStateKey);
      } catch (error) {
        // Session storage is optional.
      }
    }

    clearState() {
      try {
        window.sessionStorage.removeItem(this.stateKey);
      } catch (error) {
        // Session storage is optional.
      }
    }
  }

  customElements.define('collection-load-more', CollectionLoadMore);
}
